import React, { useState, useEffect, useRef } from 'react';
import { DatabaseService } from '../../services/DatabaseService';

const ResultsAnimation = ({ pollId, onFinish }) => {
  // Data states
  const [pollData, setPollData] = useState(null);
  const [votes, setVotes] = useState([]);
  const [roundResults, setRoundResults] = useState([]);
  const [quota, setQuota] = useState(0);
  const [winner, setWinner] = useState(null);
  const [isTie, setIsTie] = useState(false);
  const [tiedOptions, setTiedOptions] = useState([]);
  
  // Animation control states
  const [currentRound, setCurrentRound] = useState(0);
  const [animationStep, setAnimationStep] = useState(0);
  const [voteIndex, setVoteIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [announcement, setAnnouncement] = useState('');
  const [explanationVisible, setExplanationVisible] = useState(false);
  const [error, setError] = useState('');
  
  // Animation status tracking
  const [animationStatus, setAnimationStatus] = useState({
    currentAnimation: null,
    eliminationInProgress: false,
    lastStateChange: Date.now()
  });
  
  // Store refs to timeouts for cleanup
  const timeoutRefs = useRef({});
  
  // Base animation speed in milliseconds
  const baseAnimationSpeed = 800;
  
  // Get actual animation speed based on playback speed
  const getAnimationSpeed = () => baseAnimationSpeed / playbackSpeed;
  
  // Color palette for option bars
  const optionColors = [
    '#4C1D95', '#1E40AF', '#047857', '#B45309', '#BE185D', 
    '#7C3AED', '#059669', '#2563EB', '#DC2626', '#0891B2'
  ];

  // Clean up all timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts in the refs object
      Object.values(timeoutRefs.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);
  
  // Load poll data and votes
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching poll results for:', pollId);
        const results = await DatabaseService.getPollResults(pollId);
        console.log('Poll data:', results.pollData);
        console.log('Votes received:', results.votes);
        
        setPollData(results.pollData);
        
        // Store real votes for animation
        const validVotes = results.votes.filter(vote => vote.rankings && vote.rankings.rank1);
        console.log('Valid votes for animation:', validVotes.length);
        setVotes(validVotes);
        
        // Initialize counts object for all options
        const initialCounts = {};
        results.pollData.options.forEach(option => {
          initialCounts[option.id] = 0;
        });
        
        // Calculate quota - using Droop quota: floor(votes / (seats + 1)) + 1
        const totalValidVotes = validVotes.length;
        const calculatedQuota = Math.floor(totalValidVotes / (1 + 1)) + 1;
        console.log('Calculated quota:', calculatedQuota, 'from', totalValidVotes, 'votes');
        setQuota(calculatedQuota > 0 ? calculatedQuota : 1); // Minimum quota of 1
        
        // Set up empty round results with initialized counts
        setRoundResults([
          {
            round: 1,
            counts: {...initialCounts},
            winners: [],
            eliminated: null
          }
        ]);
        
        setVoteIndex(0);
      } catch (err) {
        console.error('Failed to load results:', err);
        setError(`Failed to load results: ${err.message}`);
      }
    };
    
    fetchData();
  }, [pollId]);
  
  // Helper to clear a specific timeout
  const clearAnimationTimeout = (key) => {
    if (timeoutRefs.current[key]) {
      clearTimeout(timeoutRefs.current[key]);
      timeoutRefs.current[key] = null;
    }
  };
  
  // Set a timeout and store its reference
  const setAnimationTimeout = (callback, delay, key) => {
    // Clear any existing timeout with the same key
    clearAnimationTimeout(key);
    
    // Set new timeout and store reference
    timeoutRefs.current[key] = setTimeout(() => {
      callback();
      timeoutRefs.current[key] = null;
    }, delay);
    
    return timeoutRefs.current[key];
  };
  
  // Reset animation state completely
  const resetAnimationState = () => {
    console.log("Resetting animation state");
    
    // Clear all timeouts
    Object.keys(timeoutRefs.current).forEach(key => {
      clearAnimationTimeout(key);
    });
    
    // Reset animation status
    setAnimationStatus({
      currentAnimation: null,
      eliminationInProgress: false,
      lastStateChange: Date.now()
    });
  };
  
  // Process next animation step after resetting state
  const processNextAnimationStep = () => {
    // Allow a small delay for state to update
    setTimeout(() => {
      if (voteIndex >= votes.length && animationStep === 0) {
        // Force start elimination process
        setAnimationStep(1);
      } else if (animationStatus.eliminationInProgress) {
        // Increment to next elimination round
        setAnimationStep(prevStep => prevStep + 1);
      }
    }, 50);
  };
  
  // Process votes animation one by one
  useEffect(() => {
    // Skip if we're paused or animation is complete
    if (isPaused || animationComplete) return;
    
    // Skip if we're in elimination or don't have required data
    if (!pollData || votes.length === 0 || animationStatus.currentAnimation || animationStatus.eliminationInProgress) return;
    
    // If we've counted all votes, move to elimination step
    if (voteIndex >= votes.length) {
      console.log('All votes have been counted. Moving to check for winners...');
      const newRoundResults = [...roundResults];
      const currentRoundData = { ...newRoundResults[currentRound] };
      
      // Find the option with the highest vote count
      let highestCount = 0;
      let highestOptionId = null;
      
      Object.entries(currentRoundData.counts).forEach(([optionId, count]) => {
        if (count > highestCount) {
          highestCount = count;
          highestOptionId = optionId;
        }
      });
      
      // Check if highest vote-getter meets the quota
      if (highestOptionId && highestCount >= quota) {
        // We have a winner
        currentRoundData.winners = [highestOptionId];
        newRoundResults[currentRound] = currentRoundData;
        setRoundResults([...newRoundResults]);
        
        const winnerOption = pollData.options.find(option => option.id === highestOptionId);
        setWinner(winnerOption);
        setAnnouncement(`${winnerOption.text} wins with ${highestCount} votes, reaching the quota of ${quota}!`);
        
        setAnimationTimeout(() => {
          setAnimationComplete(true);
        }, getAnimationSpeed() * 2, "completeAnimation");
      } else {
        // No winner yet, explain moving to elimination
        setAnnouncement(`No option reached the quota of ${quota} votes. We'll now eliminate the option with the fewest votes and redistribute its votes.`);
        
        // Move to elimination step after a delay
        setAnimationTimeout(() => {
          console.log('Moving to elimination step');
          setAnimationStep(1);
        }, getAnimationSpeed() * 2, "moveToElimination");
      }
      return;
    }
    
    // Start the vote counting animation
    const countNextVote = () => {
      setAnimationStatus(prev => ({
        ...prev,
        currentAnimation: 'counting',
        lastStateChange: Date.now()
      }));
      
      console.log('Counting vote', voteIndex + 1, 'of', votes.length);
      
      // Clone current round results
      const newRoundResults = [...roundResults];
      const currentRoundData = { ...newRoundResults[currentRound] };
      
      // Get the next vote to count
      const vote = votes[voteIndex];
      console.log('Processing vote:', vote);
      
      // Get first preference from the vote
      const firstPreference = vote.rankings.rank1;
      console.log('First preference:', firstPreference);
      
      // Update count for this option
      if (firstPreference) {
        currentRoundData.counts[firstPreference] = (currentRoundData.counts[firstPreference] || 0) + 1;
        console.log('Updated counts:', currentRoundData.counts);
        
        // Update state to reflect this vote
        newRoundResults[currentRound] = currentRoundData;
        setRoundResults([...newRoundResults]);
        
        // Update announcement to show progress
        const optionText = pollData.options.find(opt => opt.id === firstPreference)?.text || firstPreference;
        setAnnouncement(`Counting vote ${voteIndex + 1} of ${votes.length}: First choice for ${optionText}`);
      }
      
      // Increment vote index
      setVoteIndex(voteIndex + 1);
      
      // Reset animation status
      setAnimationStatus(prev => ({
        ...prev,
        currentAnimation: null
      }));
    };
    
    setAnimationTimeout(countNextVote, getAnimationSpeed(), "countVote");
    
    return () => {
      clearAnimationTimeout("countVote");
    };
  }, [pollData, votes, voteIndex, currentRound, animationStep, roundResults, animationStatus.currentAnimation, 
      animationStatus.eliminationInProgress, quota, animationComplete, isPaused, playbackSpeed]);
  
  // Handle elimination process - core of the STV algorithm
  useEffect(() => {
    // Skip if we're paused or animation is complete
    if (isPaused || animationComplete) return;
    
    // Skip if we don't have the necessary data or not at elimination step
    if (!pollData || votes.length === 0 || animationStep === 0 || voteIndex < votes.length) return;
    
    // Skip if already running an elimination
    if (animationStatus.eliminationInProgress || animationStatus.currentAnimation) return;
    
    // Start new elimination round
    console.log('------ STARTING NEW ELIMINATION ROUND ------');
    console.log(`animationStep: ${animationStep}, currentRound: ${currentRound}`);
    
    // Mark that we're starting the elimination process
    setAnimationStatus({
      currentAnimation: 'elimination',
      eliminationInProgress: true,
      lastStateChange: Date.now()
    });
    
    const runElimination = async () => {
      try {
        // Step 1: Get current round data
        const prevRoundData = { ...roundResults[currentRound] };
        console.log('Current round data:', prevRoundData);
        
        // Step 2: Find the option with lowest count
        let lowestCount = Infinity;
        let lowestOptionId = null;
        
        // Only consider options that haven't been eliminated or won
        Object.entries(prevRoundData.counts).forEach(([optionId, count]) => {
          if (!prevRoundData.winners.includes(optionId) && 
              !roundResults.some(r => r.eliminated === optionId) && 
              count < lowestCount) {
            lowestCount = count;
            lowestOptionId = optionId;
          }
        });
        
        console.log(`Lowest option to eliminate: ${lowestOptionId} with count: ${lowestCount}`);
        
        // If no option to eliminate, handle special cases
        if (!lowestOptionId) {
          console.log('No option to eliminate - checking for default winner');
          
          // Find remaining option(s)
          const remainingOptions = Object.entries(prevRoundData.counts)
            .filter(([optionId]) => 
              !prevRoundData.winners.includes(optionId) && 
              !roundResults.some(r => r.eliminated === optionId))
            .sort((a, b) => b[1] - a[1]); // Sort by highest count
          
          if (remainingOptions.length === 1) {
            // One option remains - it's the winner by default
            const winnerId = remainingOptions[0][0];
            const winnerOption = pollData.options.find(opt => opt.id === winnerId);
            const winnerCount = remainingOptions[0][1];
            
            // Update round data with the winner
            const updatedRound = { ...prevRoundData, winners: [winnerId] };
            const newRoundResults = [...roundResults];
            newRoundResults[currentRound] = updatedRound;
            
            // Update state
            setRoundResults(newRoundResults);
            setWinner(winnerOption);
            setAnnouncement(`${winnerOption.text} wins with ${winnerCount} votes as the last remaining option!`);
            
            // Reset animation status
            setAnimationStatus({
              currentAnimation: null,
              eliminationInProgress: false,
              lastStateChange: Date.now()
            });
            
            // Mark animation as complete
            setAnimationTimeout(() => {
              setAnimationComplete(true);
            }, getAnimationSpeed() * 2, "completeAnimation");
            
            return;
          } else if (remainingOptions.length > 1) {
            // Check if we have a tie (multiple options with exact same count)
            const firstCount = remainingOptions[0][1];
            const allSameCount = remainingOptions.every(([, count]) => count === firstCount);
            
            if (allSameCount && remainingOptions.length === 2) {
              // Perfect tie between two options - need a runoff
              console.log('Perfect tie detected between two options:', remainingOptions);
              
              // Get the tied options
              const tiedOptionIds = remainingOptions.map(([id]) => id);
              const tiedOptionObjects = tiedOptionIds.map(id => 
                pollData.options.find(opt => opt.id === id)
              );
              
              // Set tie state
              setIsTie(true);
              setTiedOptions(tiedOptionObjects);
              
              // Update round data
              const updatedRound = { 
                ...prevRoundData, 
                isTie: true, 
                tiedOptions: tiedOptionIds,
                note: "Perfect tie detected - runoff vote needed"
              };
              
              const newRoundResults = [...roundResults];
              newRoundResults[currentRound] = updatedRound;
              setRoundResults(newRoundResults);
              
              // Set announcement about tie
              setAnnouncement(
                `We have a tie! ${tiedOptionObjects[0].text} and ${tiedOptionObjects[1].text} both have exactly ${firstCount} votes. A runoff vote would be needed to determine the winner.`
              );
              
              // Reset animation status
              setAnimationStatus({
                currentAnimation: null,
                eliminationInProgress: false,
                lastStateChange: Date.now()
              });
              
              // Mark animation as complete
              setAnimationTimeout(() => {
                setAnimationComplete(true);
              }, getAnimationSpeed() * 2, "completeAnimation");
              
              return;
            }
            
            // If not a perfect tie or more than 2 options, pick the highest count option
            const highestOptionEntry = remainingOptions[0];
            const winnerId = highestOptionEntry[0];
            const winnerOption = pollData.options.find(opt => opt.id === winnerId);
            const winnerCount = highestOptionEntry[1];
            
            // Update round data with the winner
            const updatedRound = { ...prevRoundData, winners: [winnerId] };
            const newRoundResults = [...roundResults];
            newRoundResults[currentRound] = updatedRound;
            
            // Update state
            setRoundResults(newRoundResults);
            setWinner(winnerOption);
            setAnnouncement(`${winnerOption.text} wins with ${winnerCount} votes as the option with the highest count among tied options!`);
            
            // Reset animation status
            setAnimationStatus({
              currentAnimation: null,
              eliminationInProgress: false,
              lastStateChange: Date.now()
            });
            
            // Mark animation as complete
            setAnimationTimeout(() => {
              setAnimationComplete(true);
            }, getAnimationSpeed() * 2, "completeAnimation");
            
            return;
          } else {
            // No options left check - should never happen
            console.error('No options left to process');
            setError('Error: No options left to process in the animation');
            
            // Reset animation status
            setAnimationStatus({
              currentAnimation: null,
              eliminationInProgress: false,
              lastStateChange: Date.now()
            });
            
            return;
          }
        }
        
        // Step 3: Announce the elimination
        const eliminatedOption = pollData.options.find(opt => opt.id === lowestOptionId);
        console.log(`Announcing elimination of ${eliminatedOption.text} with ${lowestCount} votes`);
        
        setAnnouncement(`Eliminating ${eliminatedOption.text} with ${lowestCount} votes. Voters who chose this option will have their votes transferred to their next preference.`);
        
        // Step 4: After a delay, update the UI and prepare for next round
        await new Promise(resolve => {
          setAnimationTimeout(() => {
            resolve();
          }, getAnimationSpeed() * 2, "eliminationDelay");
        });
        
        // Step 5: Mark the option as eliminated in current round
        const updatedRound = { ...prevRoundData, eliminated: lowestOptionId };
        
        // Step 6: Create the next round data
        const nextRound = {
          round: currentRound + 2, // +2 because rounds are 1-indexed in display
          counts: { ...prevRoundData.counts }, // Copy counts from current round
          winners: [...prevRoundData.winners], // Keep any winners
          eliminated: null // No elimination yet in the new round
        };
        
        // Set eliminated option count to 0 in next round
        nextRound.counts[lowestOptionId] = 0;
        
        // Step 7: Find votes to redistribute
        const votesToRedistribute = votes.filter(vote => {
          // Get all preferences in order
          const preferences = Object.entries(vote.rankings)
            .map(([key, value]) => ({ 
              rank: parseInt(key.replace('rank', '')), 
              optionId: value 
            }))
            .sort((a, b) => a.rank - b.rank);
          
          // Get first valid preference (not eliminated in previous rounds)
          const validPreferences = preferences.filter(pref => 
            !roundResults.some(r => r.eliminated === pref.optionId));
          
          // Check if first valid preference is the one we're eliminating
          return validPreferences.length > 0 && validPreferences[0].optionId === lowestOptionId;
        });
        
        console.log(`Found ${votesToRedistribute.length} votes to redistribute`);
        
        // Step 8: Redistribute votes
        let redistributedCount = 0;
        
        for (const vote of votesToRedistribute) {
          // Get all preferences in rank order
          const preferences = Object.entries(vote.rankings)
            .map(([key, value]) => ({ 
              rank: parseInt(key.replace('rank', '')), 
              optionId: value 
            }))
            .sort((a, b) => a.rank - b.rank);
          
          // Find next valid preference that's not eliminated
          const validNextPreferences = preferences.filter(pref => 
            pref.optionId !== lowestOptionId && 
            !roundResults.some(r => r.eliminated === pref.optionId));
          
          if (validNextPreferences.length > 0) {
            const nextPref = validNextPreferences[0].optionId;
            nextRound.counts[nextPref] = (nextRound.counts[nextPref] || 0) + 1;
            redistributedCount++;
          }
        }
        
        // Step 9: Update the rounds data
        const newRoundResults = [...roundResults];
        newRoundResults[currentRound] = updatedRound; // Update current round
        newRoundResults.push(nextRound); // Add new round
        
        setRoundResults(newRoundResults);
        setCurrentRound(currentRound + 1);
        
        // Step 10: Announce the redistribution
        setAnnouncement(
          `Moved to Round ${currentRound + 2}. ${redistributedCount} vote${redistributedCount !== 1 ? 's' : ''} from ${eliminatedOption.text} ${redistributedCount !== 1 ? 'have' : 'has'} been redistributed to voters' next choices.`
        );
        
        // Step 11: Check if we have a winner in the new round
        const highestCount = Math.max(...Object.values(nextRound.counts));
        const highestOptionId = Object.entries(nextRound.counts)
          .filter(([id]) => !roundResults.some(r => r.eliminated === id))
          .sort((a, b) => b[1] - a[1])[0]?.[0];
        
        if (highestOptionId && highestCount >= quota) {
          // We have a winner
          console.log(`Winner found: ${highestOptionId} with count ${highestCount}`);
          
          const winnerOption = pollData.options.find(opt => opt.id === highestOptionId);
          nextRound.winners = [highestOptionId];
          newRoundResults[currentRound + 1] = nextRound;
          
          setRoundResults(newRoundResults);
          setWinner(winnerOption);
          setAnnouncement(`${winnerOption.text} wins with ${highestCount} votes after redistribution, reaching the quota of ${quota}!`);
          
          // Reset animation status
          setAnimationStatus({
            currentAnimation: null,
            eliminationInProgress: false,
            lastStateChange: Date.now()
          });
          
          // Mark animation as complete
          setAnimationTimeout(() => {
            setAnimationComplete(true);
          }, getAnimationSpeed() * 2, "completeAnimation");
          
          return;
        } 
        
        // Check if only one option remains
        const remainingOptions = Object.entries(nextRound.counts)
          .filter(([id, count]) => 
            !roundResults.some(r => r.eliminated === id) && 
            count > 0);
        
        if (remainingOptions.length === 1) {
          // One option remains with votes - it's the winner
          const winnerId = remainingOptions[0][0];
          const winnerCount = remainingOptions[0][1];
          const winnerOption = pollData.options.find(opt => opt.id === winnerId);
          
          nextRound.winners = [winnerId];
          newRoundResults[currentRound + 1] = nextRound;
          
          setRoundResults(newRoundResults);
          setWinner(winnerOption);
          setAnnouncement(`${winnerOption.text} wins with ${winnerCount} votes as the last remaining option!`);
          
          // Reset animation status
          setAnimationStatus({
            currentAnimation: null,
            eliminationInProgress: false,
            lastStateChange: Date.now()
          });
          
          // Mark animation as complete
          setAnimationTimeout(() => {
            setAnimationComplete(true);
          }, getAnimationSpeed() * 2, "completeAnimation");
          
          return;
        }
        
        // Check if we have a perfect tie between two options
        if (remainingOptions.length === 2) {
          const [option1, option2] = remainingOptions;
          
          if (option1[1] === option2[1]) {
            // Perfect tie between two options
            console.log('Perfect tie detected between two remaining options:', option1, option2);
            
            const tiedOptionIds = [option1[0], option2[0]];
            const tiedOptionObjects = tiedOptionIds.map(id => 
              pollData.options.find(opt => opt.id === id)
            );
            
            // Set tie state
            setIsTie(true);
            setTiedOptions(tiedOptionObjects);
            
            // Update round data
            nextRound.isTie = true;
            nextRound.tiedOptions = tiedOptionIds;
            nextRound.note = "Perfect tie detected - runoff vote needed";
            
            newRoundResults[currentRound + 1] = nextRound;
            setRoundResults(newRoundResults);
            
            // Set announcement about tie
            setAnnouncement(
              `We have a tie! ${tiedOptionObjects[0].text} and ${tiedOptionObjects[1].text} both have exactly ${option1[1]} votes after redistribution. A runoff vote would be needed to determine the winner.`
            );
            
            // Reset animation status
            setAnimationStatus({
              currentAnimation: null,
              eliminationInProgress: false,
              lastStateChange: Date.now()
            });
            
            // Mark animation as complete
            setAnimationTimeout(() => {
              setAnimationComplete(true);
            }, getAnimationSpeed() * 2, "completeAnimation");
            
            return;
          }
        }
        
        // Continue to next round after a delay
        setAnimationTimeout(() => {
          // Reset animation status
          setAnimationStatus({
            currentAnimation: null,
            eliminationInProgress: false,
            lastStateChange: Date.now()
          });
          
          // Trigger next round
          setAnimationStep(animationStep + 1);
        }, getAnimationSpeed() * 2, "nextRound");
        
      } catch (error) {
        console.error('Error in elimination process:', error);
        
        // Reset animation state and try to recover
        resetAnimationState();
        processNextAnimationStep();
      }
    };
    
    // Run the elimination process
    runElimination();
    
    // Cleanup function
    return () => {
      Object.keys(timeoutRefs.current).forEach(key => {
        if (key.startsWith("elimination")) {
          clearAnimationTimeout(key);
        }
      });
    };
  }, [animationStep, pollData, votes, currentRound, roundResults, quota, isPaused, animationComplete, playbackSpeed]);
  
  // Automatically detect and fix stuck animations
  useEffect(() => {
    const stuckDetectionInterval = setInterval(() => {
      const { currentAnimation, eliminationInProgress, lastStateChange } = animationStatus;
      
      // Check if animation appears stuck
      if ((currentAnimation || eliminationInProgress) && 
          Date.now() - lastStateChange > 3000) {
        console.log("Animation appears stuck for more than 3 seconds, resetting");
        
        // Reset animation state and try to continue
        resetAnimationState();
        processNextAnimationStep();
      }
    }, 1000);
    
    return () => clearInterval(stuckDetectionInterval);
  }, [animationStatus]);
  
  // Animation control functions
  const togglePause = () => {
    setIsPaused(prev => !prev);
  };
  
  const restart = () => {
    // Clear all running timeouts
    Object.keys(timeoutRefs.current).forEach(key => {
      clearAnimationTimeout(key);
    });
    
    // Reset all animation state
    setVoteIndex(0);
    setCurrentRound(0);
    setAnimationStep(0);
    setAnimationStatus({
      currentAnimation: null,
      eliminationInProgress: false,
      lastStateChange: Date.now()
    });
    setAnimationComplete(false);
    setWinner(null);
    setIsTie(false);
    setTiedOptions([]);
    setAnnouncement('');
    
    // Reset round results to initial state
    if (pollData && pollData.options) {
      const initialCounts = {};
      pollData.options.forEach(option => {
        initialCounts[option.id] = 0;
      });
      
      setRoundResults([
        {
          round: 1,
          counts: {...initialCounts},
          winners: [],
          eliminated: null
        }
      ]);
    }
    
    // Resume animation if it was paused
    setIsPaused(false);
  };
  
  // Manually advance to next step when paused
  const goToNextStep = () => {
    if (animationComplete) return;
    
    // If we're in the initial vote counting phase
    if (voteIndex < votes.length) {
      // Process the next vote
      const newRoundResults = [...roundResults];
      const currentRoundData = { ...newRoundResults[currentRound] };
      
      // Get the next vote to count
      const vote = votes[voteIndex];
      
      // Get first preference from the vote
      const firstPreference = vote.rankings.rank1;
      
      // Update count for this option
      if (firstPreference) {
        currentRoundData.counts[firstPreference] = (currentRoundData.counts[firstPreference] || 0) + 1;
        
        // Update state to reflect this vote
        newRoundResults[currentRound] = currentRoundData;
        setRoundResults([...newRoundResults]);
        
        // Update announcement
        const optionText = pollData.options.find(opt => opt.id === firstPreference)?.text || firstPreference;
        setAnnouncement(`Counting vote ${voteIndex + 1} of ${votes.length}: First choice for ${optionText}`);
      }
      
      // Move to next vote
      setVoteIndex(voteIndex + 1);
    } 
    // If we're at the end of vote counting but haven't moved to elimination
    else if (voteIndex >= votes.length && animationStep === 0) {
      setAnimationStep(1);
    }
    // In elimination phase, but not currently processing
    else if (!animationStatus.eliminationInProgress) {
      // Trigger the next elimination round
      setAnimationStep(prev => prev + 1);
    } else {
      // If we're stuck in elimination, reset and try again
      resetAnimationState();
      processNextAnimationStep();
    }
  };
  
  // Go back to beginning
  const goToPreviousStep = () => {
    // Simple implementation: restart from beginning
    restart();
  };
  
  // Change animation speed
  const changeSpeed = (newSpeed) => {
    setPlaybackSpeed(newSpeed);
  };
  
// Toggle STV explanation panel
const toggleExplanation = () => {
  setExplanationVisible(!explanationVisible);
};

// Display error message if any
if (error) {
  return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <span className="block sm:inline">{error}</span>
    </div>
  );
}

// Show loading spinner while fetching poll data
if (!pollData) {
  return (
    <div className="flex justify-center items-center py-8">
      <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>
  );
}

// Render the current round status
const renderRoundStatus = () => {
  const round = roundResults[currentRound];
  if (!round) return null;
  
  return (
    <div className="mb-4">
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Round {round.round} {animationStatus.currentAnimation === 'counting' ? '- Counting Votes' : 
                       animationStatus.currentAnimation === 'elimination' ? '- Redistributing Votes' : ''}
      </h3>
      
      {round.eliminated && (
        <div className="bg-red-100 p-3 rounded-md mb-4">
          <p className="text-red-800">
            Option "{pollData.options.find(o => o.id === round.eliminated)?.text}" eliminated. 
            Votes redistributed to next preferences.
          </p>
        </div>
      )}
      
      {round.winners.length > 0 && (
        <div className="bg-green-100 p-3 rounded-md mb-4">
          <p className="text-green-800">
            Winner: {pollData.options.find(o => o.id === round.winners[0])?.text}
          </p>
        </div>
      )}
      
      {round.isTie && round.tiedOptions && (
        <div className="bg-yellow-100 p-3 rounded-md mb-4">
          <p className="text-yellow-800">
            Tie detected between: {round.tiedOptions.map(id => 
              pollData.options.find(o => o.id === id)?.text).join(' and ')}
          </p>
        </div>
      )}
      
      <p className="text-sm text-gray-500 mb-2">
        Quota to win: {quota} votes ({votes.length > 0 ? Math.round(quota / votes.length * 100) : 0}% of total)
      </p>
      
      {announcement && (
        <div className="bg-blue-100 p-3 rounded-md mb-4">
          <p className="text-blue-800">{announcement}</p>
        </div>
      )}
      
      <p className="text-sm text-gray-500 mb-2">
        Votes counted: {Math.min(voteIndex, votes.length)} of {votes.length}
      </p>
    </div>
  );
};

// Render vote count bars for each option
const renderVoteBars = () => {
  if (!roundResults[currentRound]) return null;
  
  const round = roundResults[currentRound];
  // Use total votes for max value or at least the quota
  const maxVotes = Math.max(...Object.values(round.counts), quota, 1);
  
  return (
    <div className="space-y-4 mt-4">
      {pollData.options.map((option, index) => {
        const votes = round.counts[option.id] || 0;
        const percentage = maxVotes ? (votes / maxVotes) * 100 : 0;
        const color = optionColors[index % optionColors.length];
        
        // In STV, only one option can be the winner
        const isWinner = round.winners.length > 0 && round.winners[0] === option.id;
        
        const isEliminated = round.eliminated === option.id || 
                             roundResults.some(r => r.eliminated === option.id);
        
        // Check if this option is part of a tie
        const isTied = round.isTie && round.tiedOptions && round.tiedOptions.includes(option.id);
        
        return (
          <div key={option.id} className="relative">
            <div className="flex items-center mb-1">
              <div className="w-6 h-6 rounded-full mr-2" style={{ backgroundColor: color }}></div>
              <span className="font-medium text-gray-900">{option.text}</span>
              {isWinner && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Winner
                </span>
              )}
              {isEliminated && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Eliminated
                </span>
              )}
              {isTied && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Tied
                </span>
              )}
            </div>
            <div className="relative h-8 rounded-full bg-gray-200 overflow-hidden">
              <div 
                className="absolute left-0 top-0 h-full"
                style={{ 
                  width: `${percentage}%`, 
                  backgroundColor: color,
                  transition: 'width 0.5s ease-in-out',
                  opacity: isEliminated ? 0.5 : 1
                }}
              ></div>
              <div className="absolute inset-0 flex items-center justify-end px-3">
                <span className="text-sm font-medium text-gray-900">{votes}</span>
              </div>
              
              {/* Quota line */}
              {quota > 0 && maxVotes > 0 && (
                <div 
                  className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-500"
                  style={{ 
                    left: `${(quota / maxVotes) * 100}%`,
                    opacity: 0.7
                  }}
                >
                  <div className="absolute -top-6 -translate-x-1/2 text-xs text-red-600 font-medium">
                    Quota ({quota})
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Render winner or tie results section
const renderWinnerSection = () => {
  if (!winner && !isTie) return null;
  
  if (winner) {
    return (
      <div className="mb-6 bg-green-50 p-6 rounded-lg border border-green-200">
        <div className="flex items-center mb-4">
          <div className="bg-green-100 rounded-full p-3 text-green-800">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="ml-3 text-xl font-bold text-green-900">
            Winner: {winner.text}
          </h3>
        </div>
        <p className="text-green-800">
          After {roundResults.length} round{roundResults.length > 1 ? 's' : ''} of counting with {votes.length} total votes.
        </p>
      </div>
    );
  }
  
  // Render tie information
  if (isTie && tiedOptions.length === 2) {
    return (
      <div className="mb-6 bg-yellow-50 p-6 rounded-lg border border-yellow-200">
        <div className="flex items-center mb-4">
          <div className="bg-yellow-100 rounded-full p-3 text-yellow-800">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="ml-3 text-xl font-bold text-yellow-900">
            Tie Detected - Runoff Vote Needed
          </h3>
        </div>
        <p className="text-yellow-800 mb-4">
          After {roundResults.length} round{roundResults.length > 1 ? 's' : ''} of counting with {votes.length} total votes,
          we have a perfect tie between the final two options:
        </p>
        <div className="bg-white rounded-lg p-4 border border-yellow-200 mb-4">
          <div className="grid grid-cols-2 gap-4">
            {tiedOptions.map((option, index) => (
              <div key={index} className="flex items-center p-3 bg-yellow-50 rounded-lg">
                <div 
                  className="w-6 h-6 rounded-full mr-2" 
                  style={{ backgroundColor: optionColors[pollData.options.findIndex(o => o.id === option.id) % optionColors.length] }}
                ></div>
                <span className="font-medium">{option.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">What happens next?</h4>
          <p className="text-blue-700">
            In a ranked choice voting system, when there's an exact tie after all redistributions, 
            the standard procedure is to conduct a runoff vote between the tied options.
            You should create a new poll with just these two options and have everyone vote again.
          </p>
        </div>
      </div>
    );
  }
  
  return null;
};

// Render RCV explanation panel
const renderExplanation = () => {
  if (!explanationVisible) return null;
  
  return (
    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 mb-6">
      <h4 className="font-medium text-indigo-900 mb-2">How Ranked Choice Voting Works</h4>
      <div className="text-sm text-indigo-800 space-y-2">
        <p><strong>Step 1: Vote Counting</strong> - Each voter ranks options in order of preference. Initially, only first choices are counted.</p>
        <p><strong>Step 2: Quota Calculation</strong> - A "quota" or threshold of votes needed to win is calculated: (Total Votes ÷ (Seats + 1)) + 1. For single winner elections, this simplifies to (Total Votes ÷ 2) + 1.</p>
        <p><strong>Step 3: Winner Check</strong> - If any option reaches or exceeds the quota, it wins immediately.</p>
        <p><strong>Step 4: Elimination</strong> - If no option reaches the quota, the option with the fewest votes is eliminated.</p>
        <p><strong>Step 5: Vote Redistribution</strong> - Votes for the eliminated option are redistributed to their next ranked choice.</p>
        <p><strong>Step 6: Repeat</strong> - Steps 3-5 are repeated until a winner is found or only one option remains.</p>
        <p><strong>Tie Handling</strong> - If there's an exact tie between the final two options, a separate runoff vote would be conducted.</p>
      </div>
      <button 
        onClick={toggleExplanation}
        className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
      >
        Hide Explanation
      </button>
    </div>
  );
};

// Render animation control panel
const renderAnimationControls = () => {
  return (
    <div className="bg-gray-50 p-4 rounded-lg border mb-6">
      <h4 className="font-medium text-gray-900 mb-3">Animation Controls</h4>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={togglePause}
          className="px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
          disabled={animationComplete}
        >
          {isPaused ? 'Play' : 'Pause'}
        </button>
        
        <button
          onClick={restart}
          className="px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
        >
          Restart
        </button>
        
        {isPaused && (
          <>
            <button
              onClick={goToPreviousStep}
              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              disabled={currentRound === 0 && voteIndex === 0}
            >
              Previous
            </button>
            
            <button
              onClick={goToNextStep}
              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              disabled={animationComplete}
            >
              Next
            </button>
          </>
        )}
        
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-gray-600">Speed:</span>
          <select 
            value={playbackSpeed}
            onChange={(e) => changeSpeed(parseFloat(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
          >
            <option value="0.5">0.5x</option>
            <option value="1">1x</option>
            <option value="2">2x</option>
            <option value="4">4x</option>
          </select>
        </div>
        
        <button
          onClick={toggleExplanation}
          className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition ml-2"
        >
          {explanationVisible ? 'Hide Help' : 'Show Help'}
        </button>
      </div>
    </div>
  );
};

// Main component render
return (
  <div className="relative">
    {votes.length === 0 ? (
      <div className="text-center py-8">
        <div className="mx-auto flex justify-center mb-4">
          <svg className="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">
          Preparing vote calculation...
        </h3>
      </div>
    ) : (
      <div>
        {renderAnimationControls()}
        
        {explanationVisible && renderExplanation()}
        
        {animationComplete && renderWinnerSection()}
        
        {renderRoundStatus()}
        
        {renderVoteBars()}
        
        {/* Instructions for creating a runoff poll if there was a tie */}
        {animationComplete && isTie && (
          <div className="mt-6 bg-white p-4 rounded-lg border shadow-sm">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Creating a Runoff Vote</h3>
            <p className="text-gray-600 mb-4">
              To create a runoff vote between the tied options, follow these steps:
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-gray-600 mb-4">
              <li>Go to the dashboard and click "Create New Poll"</li>
              <li>Title it something like "{pollData.title} - Runoff Vote"</li>
              <li>Add only the two tied options: {tiedOptions.map(opt => opt.text).join(' and ')}</li>
              <li>Share the new poll with the same participants</li>
              <li>In this runoff, a simple majority (most votes) will determine the winner</li>
            </ol>
            <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
              <p className="text-yellow-700 text-sm">
                <strong>Note:</strong> This is a common approach in ranked choice voting systems when exact ties occur
                after all possible vote redistributions have been made.
              </p>
            </div>
          </div>
        )}
        
        {/* Display close animation button when complete */}
        {animationComplete && (
          <div className="mt-8 flex justify-end">
            <button
              onClick={onFinish}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Close Animation
            </button>
          </div>
        )}
      </div>
    )}
  </div>
);
};

export default ResultsAnimation;